// Inspector geometry (DES2): surface → host coordinates, clipping, the identity clock that
// throws away stale answers, and where floating labels and panels go. Pure; no DOM.
import type { GeoIdentity, Rect } from './types';

/** Bounds from INS C16: eight rects per target, finite, inside a sane coordinate range. */
export const MAX_RECTS = 8;
const MAX_COORD = 1_000_000;

/** A rect from a surface is an observation: refuse anything not finite, negative or absurd. */
export function validRect(rect: unknown): rect is Rect {
  if (!rect || typeof rect !== 'object') return false;
  const r = rect as Record<string, unknown>;
  for (const key of ['x', 'y', 'width', 'height']) {
    const v = r[key];
    if (typeof v !== 'number' || !Number.isFinite(v) || Math.abs(v) > MAX_COORD) return false;
  }
  return (r.width as number) >= 0 && (r.height as number) >= 0;
}

/** How a surface sits in the host: its viewport's offset and its CSS scale (device frames). */
export type SurfaceTransform = { offsetX: number; offsetY: number; scale: number };

export function toHost(rect: Rect, t: SurfaceTransform): Rect {
  return {
    x: t.offsetX + rect.x * t.scale,
    y: t.offsetY + rect.y * t.scale,
    width: rect.width * t.scale,
    height: rect.height * t.scale,
  };
}

/** A host point back into surface coordinates (for hit tests inside a scaled frame). */
export function toSurface(
  point: { x: number; y: number },
  t: SurfaceTransform,
): { x: number; y: number } {
  const scale = t.scale || 1;
  return { x: (point.x - t.offsetX) / scale, y: (point.y - t.offsetY) / scale };
}

/** The part of `rect` inside `viewport`, or null when none of it is visible. */
export function clip(rect: Rect, viewport: Rect): Rect | null {
  const x1 = Math.max(rect.x, viewport.x);
  const y1 = Math.max(rect.y, viewport.y);
  const x2 = Math.min(rect.x + rect.width, viewport.x + viewport.width);
  const y2 = Math.min(rect.y + rect.height, viewport.y + viewport.height);
  if (x2 <= x1 || y2 <= y1) return null;
  return { x: x1, y: y1, width: x2 - x1, height: y2 - y1 };
}

/** Valid, transformed, clipped and bounded: what the layer may draw for one target. */
export function drawable(rects: readonly unknown[], t: SurfaceTransform, viewport: Rect): Rect[] {
  const out: Rect[] = [];
  for (const r of rects) {
    if (out.length >= MAX_RECTS) break;
    if (!validRect(r)) continue;
    const visible = clip(toHost(r, t), viewport);
    if (visible) out.push(visible);
  }
  return out;
}

/** The smallest rect around several (for a label or an anchored panel). */
export function bounds(rects: readonly Rect[]): Rect | null {
  if (!rects.length) return null;
  const x1 = Math.min(...rects.map((r) => r.x));
  const y1 = Math.min(...rects.map((r) => r.y));
  const x2 = Math.max(...rects.map((r) => r.x + r.width));
  const y2 = Math.max(...rects.map((r) => r.y + r.height));
  return { x: x1, y: y1, width: x2 - x1, height: y2 - y1 };
}

/**
 * Issues geometry identities and says whether an answer is still current. A reload or a
 * navigation bumps the document generation; scroll, zoom, scale and resize bump the epoch.
 * Only the latest hover request is current; a click's request stays current until answered
 * or superseded by another click.
 */
export class GeoClock {
  private docGeneration = 0;
  private epoch = 0;
  private seq = 0;
  private latest: Record<'hover' | 'click', string | null> = { hover: null, click: null };

  constructor(
    private sessionId: string,
    private revision: string,
  ) {}

  /** A new session or revision invalidates everything that was in flight. */
  rebind(sessionId: string, revision: string): void {
    if (sessionId === this.sessionId && revision === this.revision) return;
    this.sessionId = sessionId;
    this.revision = revision;
    this.latest = { hover: null, click: null };
    this.epoch += 1;
  }

  bumpEpoch(): void {
    this.epoch += 1;
  }

  bumpDocument(): void {
    this.docGeneration += 1;
    this.latest = { hover: null, click: null };
  }

  next(kind: 'hover' | 'click'): GeoIdentity {
    this.seq += 1;
    const requestId = `${kind}-${this.seq}`;
    this.latest[kind] = requestId;
    return this.identity(requestId);
  }

  /** The identity for a geometry read that is not a request (rect refresh). */
  current(): GeoIdentity {
    return this.identity('rects');
  }

  private identity(requestId: string): GeoIdentity {
    return {
      sessionId: this.sessionId,
      revision: this.revision,
      docGeneration: this.docGeneration,
      requestId,
      space: 'host-css',
      epoch: this.epoch,
    };
  }

  /** True when an answer to `id` may still be used. */
  isCurrent(id: GeoIdentity, kind?: 'hover' | 'click'): boolean {
    if (id.sessionId !== this.sessionId || id.revision !== this.revision) return false;
    if (id.docGeneration !== this.docGeneration || id.epoch !== this.epoch) return false;
    return kind ? this.latest[kind] === id.requestId : true;
  }
}

/**
 * A label sits outside the outline, above it when there is room, below it at the viewport
 * top. It never covers the selected text.
 */
export const LABEL_INSET_PX = 8;
export const LABEL_GAP_PX = 4;
export function labelPlacement(target: Rect, viewport: Rect, labelHeight = 22): 'top' | 'bottom' {
  return target.y - viewport.y < labelHeight + LABEL_GAP_PX ? 'bottom' : 'top';
}

/** The label stays outside the ring, 8 px from its corner. */
export function labelPosition(
  target: Rect,
  placement: 'top' | 'bottom',
  labelHeight: number,
): { x: number; y: number } {
  const y =
    placement === 'top'
      ? target.y - labelHeight - LABEL_GAP_PX
      : target.y + target.height + LABEL_GAP_PX;
  return { x: target.x + LABEL_INSET_PX, y };
}

/**
 * Where a floating panel goes against a target: below it if it fits, otherwise above, and
 * otherwise on the side with more space, with a scrolling height limit. Vertical clamping
 * must never move the panel over the target.
 */
export function placePanel(
  target: Rect,
  panel: { width: number; height: number },
  viewport: Rect,
  gap = 8,
): { x: number; y: number; placement: 'below' | 'above'; maxHeight: number } {
  const below = target.y + target.height + gap;
  const belowRoom = Math.max(0, viewport.y + viewport.height - below);
  const aboveRoom = Math.max(0, target.y - gap - viewport.y);
  const fitsBelow = panel.height <= belowRoom;
  const fitsAbove = panel.height <= aboveRoom;
  const placement = fitsBelow || (!fitsAbove && belowRoom >= aboveRoom) ? 'below' : 'above';
  const maxHeight = placement === 'below' ? belowRoom : aboveRoom;
  const height = Math.min(panel.height, maxHeight);
  const y = placement === 'below' ? below : target.y - gap - height;
  const maxX = viewport.x + viewport.width - panel.width;
  const x = Math.max(viewport.x, Math.min(target.x, maxX));
  return { x, y, placement, maxHeight };
}
